import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, TextField, Grid, Button, FormControl, InputLabel, Select, MenuItem, Checkbox, FormControlLabel, Autocomplete, Paper, Snackbar, Alert, LinearProgress, IconButton, Stack, CircularProgress } from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { getData, putData } from '../../../../services/axiosInstance';
import { API_ENDPOINTS } from '../../../../services/endpoints';

// Date Picker Imports
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

const genderOptions = ['Male', 'Female', 'Other', 'Not to specify'];
const maritalStatusOptions = ['Single', 'Married', 'Divorced', 'Widowed'];
const bloodGroupOptions = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
const emergencyRelationOptions = ['Parent', 'Spouse', 'Sibling', 'Friend', 'Other'];

const indianStatesAndUTs = [
    "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa", "Gujarat",
    "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh",
    "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab",
    "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand",
    "West Bengal", "Andaman and Nicobar Islands", "Chandigarh", "Dadra and Nagar Haveli and Daman and Diu",
    "Delhi", "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry"
];

const PersonalContactDetails = ({ userId, initialData, navigationContext, onSaveAndReturn, onUpdateEmployeeSuccess, onFormCompletionChange, }) => {
    const [searchParams] = useSearchParams();
    const empIdFromUrl = searchParams.get("employeeId");
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [userDetails, setUserDetails] = useState(null);

    const [formData, setFormData] = useState({
        employeeId: '',
        firstName: '',
        lastName: '',
        gender: '',
        dateOfBirth: null,
        maritalStatus: '',
        bloodGroup: '',
        primaryPhone: '',
        personalEmail: '',
        workEmail: '',
        permanentAddressLine1: '',
        permanentAddressLine2: '',
        permanentState: '',
        permanentCity: '',
        permanentPincode: '',
        permanentCountry: 'India',
        currentAddressLine1: '',
        currentAddressLine2: '',
        currentState: '',
        currentCity: '',
        currentPincode: '',
        currentCountry: 'India',
        emergencyContacts: [{ name: '', relation: '', phone: '' }],
    });

    const [isCurrentSameAsPermanent, setIsCurrentSameAsPermanent] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    const mapDataToForm = useCallback((data) => {
        if (!data) return;

        const permanentAddress = data.addresses?.find(addr => addr.address_type === 'permanent') || {};
        const currentAddress = data.addresses?.find(addr => addr.address_type === 'current') || {};
        const loadedEmergencyContacts = data.emergency_contacts || [];

        const initialEmergencyContacts = loadedEmergencyContacts.length > 0 ?
            loadedEmergencyContacts.map(contact => ({
                name: contact.contact_name || '',
                relation: contact.relationship || '',
                phone: contact.contact_number || '',
            })) :
            [{ name: '', relation: '', phone: '' }];

        const mapped = {
            employeeId: data.emp_id || '',
            firstName: data.first_name || '',
            lastName: data.last_name || '',
            dateOfBirth: data.dob ? dayjs(data.dob) : null,
            gender: data.gender || '',
            maritalStatus: data.marital_status || '',
            bloodGroup: data.blood_group || '',
            primaryPhone: data.phone_number || '',
            personalEmail: data.personal_email || '',
            workEmail: data.email || '',
            permanentAddressLine1: permanentAddress.address_line || '',
            permanentAddressLine2: permanentAddress.address_line2 || '',
            permanentState: permanentAddress.state || '',
            permanentCity: permanentAddress.city || '',
            permanentPincode: permanentAddress.pincode || '',
            permanentCountry: permanentAddress.country || 'India',
            currentAddressLine1: currentAddress.address_line || '',
            currentAddressLine2: currentAddress.address_line2 || '',
            currentState: currentAddress.state || '',
            currentCity: currentAddress.city || '',
            currentPincode: currentAddress.pincode || '',
            currentCountry: currentAddress.country || 'India',
            emergencyContacts: initialEmergencyContacts
        };

        setFormData(mapped);

        const areAddressesSame = (
            mapped.permanentAddressLine1 === mapped.currentAddressLine1 &&
            mapped.permanentState === mapped.currentState &&
            mapped.permanentPincode === mapped.currentPincode &&
            !!mapped.permanentAddressLine1
        );
        setIsCurrentSameAsPermanent(areAddressesSame);
    }, []);

    const fetchUserDetails = useCallback(async () => {
        if (!empIdFromUrl) return;
        setLoading(true);
        try {
            const response = await getData(`${API_ENDPOINTS.fetchUserDetails}?userId=${empIdFromUrl}`);
            if (response?.status === "SUCCESS") {
                const user = response.message.user;
                setUserDetails(user);
                mapDataToForm(user);
            }
        } catch (error) {
            setSnackbar({ open: true, message: "Failed to fetch user data", severity: "error" });
        } finally {
            setLoading(false);
        }
    }, [empIdFromUrl, mapDataToForm]);

    useEffect(() => {
        fetchUserDetails();
    }, [fetchUserDetails]);

    useEffect(() => {
        if (initialData && !empIdFromUrl) {
            mapDataToForm(initialData.user || initialData);
        }
    }, [initialData, empIdFromUrl, mapDataToForm]);

    const calculateProgress = () => {
        const basicFields = [
            'firstName', 'lastName', 'dateOfBirth', 'gender',
            'primaryPhone', 'workEmail', 'permanentAddressLine1',
            'permanentState', 'permanentCity', 'permanentPincode'
        ];

        let filledCount = basicFields.filter(field => {
            const val = formData[field];
            return val !== null && val?.toString().trim() !== '';
        }).length;

        if (formData.emergencyContacts[0]?.name?.trim()) filledCount++;
        if (formData.emergencyContacts[0]?.relation?.trim()) filledCount++;
        if (formData.emergencyContacts[0]?.phone?.trim()) filledCount++;

        const totalRequiredFields = basicFields.length + 3;
        return Math.min(Math.round((filledCount / totalRequiredFields) * 100), 100);
    };

    const completion = calculateProgress();

    useEffect(() => {
        if (onFormCompletionChange) {
            onFormCompletionChange(completion);
        }
    }, [completion, onFormCompletionChange]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        // Basic validation for phone
        if (name === 'primaryPhone' && value !== '' && !/^\d+$/.test(value)) return;

        setFormData(prevData => {
            let newFormData = { ...prevData, [name]: value };

            // If "Same as Permanent" is active and we are editing a permanent field
            if (isCurrentSameAsPermanent && name.startsWith('permanent')) {
                const currentKey = name.replace('permanent', 'current');
                newFormData[currentKey] = value;
            }
            return newFormData;
        });

        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleDateChange = (newValue) => {
        setFormData(prev => ({ ...prev, dateOfBirth: newValue }));
        if (newValue && errors.dateOfBirth) {
            setErrors(prev => ({ ...prev, dateOfBirth: '' }));
        }
    };

    const handleStateChange = (fieldPrefix) => (event, newValue) => {
        const name = `${fieldPrefix}State`;
        setFormData(prevData => {
            let newFormData = { ...prevData, [name]: newValue || '' };
            if (isCurrentSameAsPermanent && fieldPrefix === 'permanent') {
                newFormData.currentState = newValue || '';
            }
            return newFormData;
        });

        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
        if (isCurrentSameAsPermanent && fieldPrefix === 'permanent' && errors.currentState) {
            setErrors(prev => ({ ...prev, currentState: '' }));
        }
    };

    const handleCheckboxChange = (e) => {
        const checked = e.target.checked;
        setIsCurrentSameAsPermanent(checked);

        if (checked) {
            setFormData((prevData) => ({
                ...prevData,
                currentAddressLine1: prevData.permanentAddressLine1,
                currentAddressLine2: prevData.permanentAddressLine2,
                currentState: prevData.permanentState,
                currentCity: prevData.permanentCity,
                currentPincode: prevData.permanentPincode,
                currentCountry: prevData.permanentCountry,
            }));

            // Logic to clear current address errors when synced
            const currentAddressFields = [
                'currentAddressLine1',
                'currentState',
                'currentCity',
                'currentPincode',
                'currentCountry',
            ];

            setErrors((prevErrors) => {
                const newErrors = { ...prevErrors };
                currentAddressFields.forEach((field) => delete newErrors[field]);
                return newErrors;
            });
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.firstName?.trim()) newErrors.firstName = 'First Name is required';
        if (!formData.lastName?.trim()) newErrors.lastName = 'Last Name is required';
        if (!formData.dateOfBirth) newErrors.dateOfBirth = 'Date of Birth is required';
        if (!formData.gender) newErrors.gender = 'Gender is required';

        if (!formData.primaryPhone || !/^\d{10}$/.test(formData.primaryPhone)) {
            newErrors.primaryPhone = 'Enter a valid 10-digit phone number';
        }

        const emailRegex = /^[a-zA-Z0-9._%+-]+@hashstack\.in$/;
        if (!formData.workEmail) {
            newErrors.workEmail = 'Work email is required';
        } else if (!emailRegex.test(formData.workEmail)) {
            newErrors.workEmail = 'Only @hashstack.in emails are allowed';
        }

        if (!formData.permanentAddressLine1?.trim()) newErrors.permanentAddressLine1 = 'Address is required';
        if (!formData.permanentState) newErrors.permanentState = 'State is required';
        if (!formData.permanentCity?.trim()) newErrors.permanentCity = 'City is required';
        if (!formData.permanentPincode || !/^\d{6}$/.test(formData.permanentPincode)) {
            newErrors.permanentPincode = 'Enter a valid 6-digit pincode';
        }
        if (!formData.permanentCountry?.trim()) newErrors.permanentCountry = 'Country is required';

        if (!isCurrentSameAsPermanent) {
            if (!formData.currentAddressLine1?.trim()) newErrors.currentAddressLine1 = 'Address is required';
            if (!formData.currentState) newErrors.currentState = 'State is required';
            if (!formData.currentCity?.trim()) newErrors.currentCity = 'City is required';
            if (!formData.currentPincode || !/^\d{6}$/.test(formData.currentPincode)) {
                newErrors.currentPincode = 'Enter a valid 6-digit pincode';
            }
            if (!formData.currentCountry?.trim()) newErrors.currentCountry = 'Country is required';
        }
        // Emergency Contact Validation
        formData.emergencyContacts.forEach((contact, index) => {
            if (index === 0) { // Primary emergency contact is required
                if (!contact.name?.trim()) newErrors[`emergencyName${index}`] = 'Name is required';
                if (!contact.relation) newErrors[`emergencyRelation${index}`] = 'Relation is required';
                if (!contact.phone || !/^\d{10}$/.test(contact.phone)) {
                    newErrors[`emergencyPhone${index}`] = 'Valid 10-digit phone required';
                }
            } else if (contact.name || contact.phone) { // If optional rows are partially filled
                if (!/^\d{10}$/.test(contact.phone)) {
                    newErrors[`emergencyPhone${index}`] = 'Valid 10-digit phone required';
                }
            }
        });

        setErrors(newErrors);

        if (Object.keys(newErrors).length > 0) {
            setSnackbar({
                open: true,
                message: "Please fill required fields.",
                severity: 'error'
            });
            return false;
        }
        return true;
    };

    const performSave = async () => {
        if (!validateForm()) return false;

        const payload = {
            userFields: {
                first_name: formData.firstName,
                last_name: formData.lastName,
                email: formData.workEmail,
                phone_number: formData.primaryPhone,
                dob: formData.dateOfBirth ? formData.dateOfBirth.format('YYYY-MM-DD') : null,
                emp_id: formData.employeeId || userDetails?.emp_id || '',
            },
            personalDetails: {
                gender: formData.gender,
                blood_group: formData.bloodGroup || null,
                employment_type_id: userDetails?.employment_type_id || 1,
                marital_status: formData.maritalStatus || null,
                personal_email: formData.personalEmail || null,
            },
            addresses: [
                {
                    address_type: "current",
                    address_line: isCurrentSameAsPermanent ? formData.permanentAddressLine1 : formData.currentAddressLine1,
                    address_line2: isCurrentSameAsPermanent ? formData.permanentAddressLine2 : formData.currentAddressLine2 || '',
                    city: isCurrentSameAsPermanent ? formData.permanentCity : formData.currentCity,
                    state: isCurrentSameAsPermanent ? formData.permanentState : formData.currentState,
                    pincode: isCurrentSameAsPermanent ? formData.permanentPincode : formData.currentPincode,
                    country: isCurrentSameAsPermanent ? formData.permanentCountry : formData.currentCountry
                },
                {
                    address_type: "permanent",
                    address_line: formData.permanentAddressLine1,
                    address_line2: formData.permanentAddressLine2 || '',
                    city: formData.permanentCity,
                    state: formData.permanentState,
                    pincode: formData.permanentPincode,
                    country: formData.permanentCountry
                }
            ],
            emergencyContacts: formData.emergencyContacts.map((contact) => ({
                contact_name: contact.name,
                relationship: contact.relation,
                contact_number: contact.phone
            }))
        };

        try {
            const targetUserId = empIdFromUrl ||userId || userDetails?.user_id;
            const response = await putData(
                `${API_ENDPOINTS.updateEmployeePersonalContact}/${targetUserId}`,
                payload
            );
            if (response.status === 'SUCCESS') {
                setSnackbar({ open: true, message: response?.message || 'Updated successfully', severity: 'success' });
                await fetchUserDetails();
                onUpdateEmployeeSuccess && onUpdateEmployeeSuccess();
                return true;
            }
        } catch (error) {
            setSnackbar({ open: true, message: error?.response?.data?.message?.msg || 'An error occurred while saving.', severity: 'error' });
            return false;
        }
    };

    const handleSaveChanges = async (e) => {
        if (e) e.preventDefault();
        const success = await performSave();
        if (success && navigationContext?.returnToRehire && onSaveAndReturn) {
            onSaveAndReturn();
        }
    };

    const handleSnackbarClose = () => setSnackbar({ ...snackbar, open: false });

    const handleAnotherEmergencyContact = () => {
        setFormData(prevData => ({
            ...prevData,
            emergencyContacts: [...prevData.emergencyContacts, { name: '', relation: '', phone: '' }]
        }));
    };

    const handleEmergencyContactChange = (index, e) => {
        const { name, value } = e.target;
        if (name === 'phone' && value !== '' && !/^\d+$/.test(value)) {
            return;
        }
        setFormData(prevData => {
            const updatedContacts = prevData.emergencyContacts.map((contact, i) =>
                i === index ? { ...contact, [name]: value } : contact
            );
            return { ...prevData, emergencyContacts: updatedContacts };
        });

        const capitalizedName = name.charAt(0).toUpperCase() + name.slice(1);
        const errorKey = `emergency${capitalizedName}${index}`;

        if (errors[errorKey]) {
            setErrors(prev => ({ ...prev, [errorKey]: '' }));
        }
    };

    const handleRemoveEmergencyContact = (indexToRemove) => {
        if (formData.emergencyContacts.length === 1) return;
        setFormData(prevData => ({
            ...prevData,
            emergencyContacts: prevData.emergencyContacts.filter((_, index) => index !== indexToRemove),
        }));
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box component="form" onSubmit={handleSaveChanges} noValidate sx={{ p: 1 }}>
                <Stack direction="row" alignItems="center" sx={{ borderBottom: '1px solid #c3c3c3', my: 2 }}>
                    <Box sx={{ flexGrow: 1, mb: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            Personal & Primary Contact Details
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                        <Box sx={{ width: 220, textAlign: 'right' }}>
                            <Typography variant="body2" fontWeight={600}>
                                {`Form Completion: ${completion}%`}
                            </Typography>
                            <LinearProgress
                                variant="determinate"
                                value={completion}
                                sx={{ height: 8, borderRadius: 5, mt: 0.5, backgroundColor: '#e0e0e0' }}
                                color={completion === 100 ? 'success' : 'primary'}
                            />
                        </Box>
                    </Stack>
                </Stack>

                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <TextField fullWidth label="Employee ID" value={formData.employeeId} InputProps={{ readOnly: true }} size='small' />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <TextField fullWidth label="First Name" name="firstName" value={formData.firstName} onChange={handleInputChange} error={!!errors.firstName} helperText={errors.firstName} required size='small' />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <TextField fullWidth label="Last Name" name="lastName" value={formData.lastName} onChange={handleInputChange} error={!!errors.lastName} helperText={errors.lastName} required size='small' />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <DatePicker
                            label="Date of Birth"
                            value={formData.dateOfBirth}
                            onChange={handleDateChange}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    size: 'small',
                                    required: true,
                                    error: !!errors.dateOfBirth,
                                    helperText: errors.dateOfBirth
                                }
                            }}
                            format="DD/MM/YYYY"
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <FormControl fullWidth required size='small' error={!!errors.gender}>
                            <InputLabel>Gender</InputLabel>
                            <Select label="Gender" name="gender" value={formData.gender} onChange={handleInputChange}>
                                {genderOptions.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                            </Select>
                            {errors.gender && <Typography variant="caption" color="error" sx={{ ml: 2 }}>{errors.gender}</Typography>}
                        </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <FormControl fullWidth size='small'>
                            <InputLabel>Marital Status</InputLabel>
                            <Select label="Marital Status" name="maritalStatus" value={formData.maritalStatus} onChange={handleInputChange}>
                                {maritalStatusOptions.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <FormControl fullWidth size='small'>
                            <InputLabel>Blood Group</InputLabel>
                            <Select label="Blood Group" name="bloodGroup" value={formData.bloodGroup} onChange={handleInputChange}>
                                {bloodGroupOptions.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <TextField fullWidth label="Contact Number" name="primaryPhone" value={formData.primaryPhone} onChange={handleInputChange} inputProps={{ maxLength: 10 }} required size='small' error={!!errors.primaryPhone} helperText={errors.primaryPhone} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <TextField fullWidth label="Work Email" name="workEmail" value={formData.workEmail} onChange={handleInputChange} type="email" required size='small' error={!!errors.workEmail} helperText={errors.workEmail} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <TextField fullWidth label="Personal Email" name="personalEmail" value={formData.personalEmail} onChange={handleInputChange} type="email" size='small' />
                    </Grid>
                </Grid>
                <Stack direction="row" alignItems="center" sx={{ borderBottom: '1px solid #c3c3c3', my: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Permanent Address
                    </Typography>
                </Stack>
                <Grid container spacing={3} sx={{ mt: 2 }}>
                    <Grid size={{ xs: 12 }}>
                        <TextField fullWidth label="Address Line 1" name="permanentAddressLine1" value={formData.permanentAddressLine1} onChange={handleInputChange} required size='small' error={!!errors.permanentAddressLine1} helperText={errors.permanentAddressLine1} />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <TextField fullWidth label="Address Line 2" name="permanentAddressLine2" value={formData.permanentAddressLine2} onChange={handleInputChange} size='small' />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Autocomplete fullWidth options={indianStatesAndUTs} value={formData.permanentState || null} onChange={handleStateChange('permanent')} renderInput={(params) => <TextField {...params} label="State / UT" required size='small' error={!!errors.permanentState} helperText={errors.permanentState} />} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <TextField fullWidth label="City" name="permanentCity" value={formData.permanentCity} onChange={handleInputChange} required size='small' error={!!errors.permanentCity} helperText={errors.permanentCity} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <TextField fullWidth label="Pincode" name="permanentPincode" value={formData.permanentPincode} onChange={handleInputChange} inputProps={{ maxLength: 6 }} required size='small' error={!!errors.permanentPincode} helperText={errors.permanentPincode} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <TextField fullWidth label="Country" name="permanentCountry" value={formData.permanentCountry} onChange={handleInputChange} required size='small' error={!!errors.permanentCountry} helperText={errors.permanentCountry} />
                    </Grid>
                </Grid>
                <Stack direction="row" alignItems="center" sx={{ borderBottom: '1px solid #c3c3c3', my: 2 }}>
                    <Box sx={{ flexGrow: 1, mb: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            Current Address
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                        <Box sx={{ width: 220, textAlign: 'right' }}>
                            <FormControlLabel control={<Checkbox checked={isCurrentSameAsPermanent} onChange={handleCheckboxChange} />} label="Same as Permanent" />
                        </Box>
                    </Stack>
                </Stack>
                <Grid container spacing={3} sx={{ mt: 2 }}>
                    <Grid size={{ xs: 12 }}>
                        <TextField fullWidth label="Address Line 1" name="currentAddressLine1" value={formData.currentAddressLine1} onChange={handleInputChange} disabled={isCurrentSameAsPermanent} required error={!!errors.currentAddressLine1} helperText={errors.currentAddressLine1} size='small' />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <TextField fullWidth label="Address Line 2" name="currentAddressLine2" value={formData.currentAddressLine2} onChange={handleInputChange} disabled={isCurrentSameAsPermanent} size='small' />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Autocomplete
                            fullWidth
                            options={indianStatesAndUTs}
                            value={formData.currentState || null}
                            onChange={handleStateChange('current')}
                            disabled={isCurrentSameAsPermanent}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="State / UT"
                                    required
                                    size='small'
                                    error={!!errors.currentState}
                                    helperText={errors.currentState}
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <TextField fullWidth label="City" name="currentCity" value={formData.currentCity} onChange={handleInputChange} disabled={isCurrentSameAsPermanent} required error={!!errors.currentCity} helperText={errors.currentCity} size='small' />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <TextField fullWidth label="Pincode" name="currentPincode" value={formData.currentPincode} onChange={handleInputChange} disabled={isCurrentSameAsPermanent} inputProps={{ maxLength: 6 }} required error={!!errors.currentPincode} helperText={errors.currentPincode} size='small' />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <TextField
                            fullWidth
                            label="Country"
                            name="currentCountry"
                            value={formData.currentCountry}
                            onChange={handleInputChange}
                            disabled={isCurrentSameAsPermanent}
                            required
                            error={!!errors.currentCountry}
                            helperText={errors.currentCountry}
                            size='small'
                        />
                    </Grid>
                </Grid>
                <Stack direction="row" alignItems="center" sx={{ borderBottom: '1px solid #c3c3c3', my: 3 }}>
                    <Box sx={{ flexGrow: 1, mb: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            Emergency Contact
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                        <Box sx={{ width: 220, textAlign: 'right' }}>
                            <Button variant="outlined" startIcon={<Add />} onClick={handleAnotherEmergencyContact} size='small' sx={{ textTransform: 'capitalize' }}>Add Contact</Button>
                        </Box>
                    </Stack>
                </Stack>
                <Grid container spacing={3} sx={{ mt: 2 }}>
                    {formData.emergencyContacts.map((contact, index) => (
                        <React.Fragment key={index}>
                            <Grid size={{ xs: 12, sm: 4 }}>
                                <TextField fullWidth label="Name" name="name" value={contact.name} onChange={(e) => handleEmergencyContactChange(index, e)} required={index === 0} size='small' error={!!errors[`emergencyName${index}`]} helperText={errors[`emergencyName${index}`]} />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 3 }}>
                                <FormControl fullWidth required={index === 0} size='small' error={!!errors[`emergencyRelation${index}`]}>
                                    <InputLabel>Relation</InputLabel>
                                    <Select label="Relation" name="relation" value={contact.relation} onChange={(e) => handleEmergencyContactChange(index, e)}>
                                        {emergencyRelationOptions.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                                    </Select>
                                    {errors[`emergencyRelation${index}`] && <Typography variant="caption" color="error" sx={{ ml: 2 }}>{errors[`emergencyRelation${index}`]}</Typography>}
                                </FormControl>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 4 }}>
                                <TextField fullWidth label="Phone" name="phone" value={contact.phone} onChange={(e) => handleEmergencyContactChange(index, e)} inputProps={{ maxLength: 10 }} required={index === 0} size='small' error={!!errors[`emergencyPhone${index}`]} helperText={errors[`emergencyPhone${index}`]} />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 1 }}>
                                <IconButton color="error" onClick={() => handleRemoveEmergencyContact(index)} disabled={formData.emergencyContacts.length === 1}><Delete /></IconButton>
                            </Grid>
                        </React.Fragment>
                    ))}
                </Grid>
                <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                    {navigationContext?.returnToRehire ? (
                        <Paper elevation={3} sx={{ width: '100%', p: 2, bgcolor: 'info.light', borderLeft: '4px solid', borderColor: 'info.main' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box sx={{ flexGrow: 1 }}>
                                    <Typography variant="subtitle1" fontWeight={600}>Rehire Verification</Typography>
                                    <Typography variant="body2">{navigationContext.stepLabel}</Typography>
                                </Box>
                                <Button variant="contained" color="info" onClick={handleSaveChanges}>Verify & Return</Button>
                            </Box>
                        </Paper>
                    ) : (
                        <Button type="submit" variant="contained" color="primary" sx={{ px: 4, py: 1.5, textTransform: 'capitalize' }}>Save Changes</Button>
                    )}
                </Box>

                <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleSnackbarClose} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                    <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
                </Snackbar>
            </Box>
        </LocalizationProvider>
    );
};

export default PersonalContactDetails;